import { makeVar, useReactiveVar } from '@apollo/client';
import { useBoolean } from '@hover/blueprint';
import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { FeatureFlag } from 'src/shared/constants';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { isBrowser } from 'src/shared/helpers/environment';
import { useTreatment } from 'src/shared/hooks';
import { Route, generatePath } from 'src/shared/route';
import type { ButtonPressed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

import type { CustomizeRouteState as RouteState } from '../schemas';
import { getImageId } from '../types';

import { useRoutes } from './useRoutes';

type CustomizeHandlerProps = {
  image: AiDisplayImage | undefined | null;
  state?: RouteState;
};

type Tracking = Partial<ButtonPressed>;

const routeToPrototype = makeVar(false);

// TODO: this is a temporary "easter egg" that allows us to hold down ⌘ + ⌥ +
// Shift while clicking the customize button and route to the new customize
// prototype route. Remove this once we've removed or graduated the prototype.
if (isBrowser()) {
  document.addEventListener('keydown', event => {
    if (event.shiftKey && event.altKey && event.metaKey) {
      routeToPrototype(true);
    }
  });

  document.addEventListener('keyup', event => {
    if (!event.shiftKey || !event.altKey || !event.metaKey) {
      routeToPrototype(false);
    }
  });
}

const generateCustomizePath = (
  image: AiDisplayImage,
  toAdvancedCustomize: boolean = false,
) => {
  const id = getImageId(image);

  if (toAdvancedCustomize) {
    return generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
      aiDisplayImageId: id,
    });
  }

  return generatePath(Route.HOMEOWNER_APP_PAINT_WALLS_DISPLAY_IMAGE.path, {
    aiDisplayImageId: id,
  });
};

const getTracking = (image: AiDisplayImage) =>
  ({
    ai_display_image_id: image.id,
    ai_original_image_id: image.aiOriginalImageId,
    ai_generated_image_id: image.aiGeneratedImage.id,
    style: image.style ?? undefined,
  } satisfies Tracking);

export const useCustomizeHandler = ({
  image,
  state,
}: CustomizeHandlerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasPreloaded, { on: onPreload }] = useBoolean(false);

  const history = useHistory();
  const { pathname } = useLocation();
  const { load, preload } = useRoutes();

  const isHoldingKeys = useReactiveVar(routeToPrototype);
  const isAdvancedCustomizeEnabled =
    useTreatment(FeatureFlag.Designer.ADVANCED_CUSTOMIZATION) ===
    FeatureFlag.Value.ON;

  const customizePathname = useMemo(() => {
    if (!image) return null;

    return generateCustomizePath(
      image,
      isHoldingKeys || isAdvancedCustomizeEnabled,
    );
  }, [image, isAdvancedCustomizeEnabled, isHoldingKeys]);

  const tracking = useMemo(() => {
    if (!image) return {};

    return getTracking(image);
  }, [image]);

  return {
    isLoading,
    tracking,
    preload: useCallback(() => {
      if (customizePathname && !hasPreloaded) {
        onPreload();
        preload(customizePathname);
      }
    }, [customizePathname, hasPreloaded, onPreload, preload]),
    onClick: useCallback(async () => {
      if (!customizePathname) {
        const error = new Error(
          'useCustomizeHandler: attempted to navigate to customize without an image',
        );

        if (__DEV__) {
          throw error;
        } else {
          logError(error);
        }

        return;
      }

      setIsLoading(true);

      try {
        await load(customizePathname);
      } finally {
        const { body } = document;

        history.push(
          customizePathname,
          state ?? {
            referrer: pathname,
            page: {
              offset: body.scrollTop,
              height: body.scrollHeight,
            },
          },
        );

        setIsLoading(false);
      }
    }, [customizePathname, history, load, pathname, state]),
  };
};
